import { formatDateLabel, formatTimeLabel } from './messageTimeline';

export function getConversationTitle(conversation) {
  if (conversation.conversation_type === 'direct' && conversation.other_participant) {
    return conversation.other_participant.username;
  }
  return conversation.name || conversation.conversation_type;
}

export function getConversationInitials(conversation) {
  const title = getConversationTitle(conversation) || '?';
  return title
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function getConversationAvatarUrl(conversation) {
  if (conversation.conversation_type === 'direct') {
    const p = conversation.other_participant || {};
    return p.profile?.photo_url || p.photo_url || null;
  }
  return null;
}

function describeMessage(message, conversation, currentUserId) {
  const senderName =
    message?.sender?.username ||
    conversation.other_participant?.username ||
    (conversation.conversation_type === 'group' ? 'Someone' : 'Message');

  const isMine =
    currentUserId != null &&
    message?.sender?.id != null &&
    Number(message.sender.id) === Number(currentUserId);

  const prefix = isMine ? 'You' : senderName;

  if (!message) return 'No messages yet';

  const placeholders = new Set([
    '[Encrypted Message]',
    '[encrypted message]',
    'encrypted message',
    'encrypted content',
    'decrypted content',
    'encrypted',
    'encrypted msg',
    'encrypted messages',
  ]);

  const candidates = [
    message.decrypted_content,
    message.content,
    message.message,
    message.body,
  ];
  const body = candidates
    .map((v) => (typeof v === 'string' ? v.trim() : v))
    .find((v) => v && !placeholders.has(v.toLowerCase()));
  if (body) return `${prefix}: ${body}`;

  // Fallbacks for non-text content
  if (message.attachments && message.attachments.length > 0) {
    const label = message.attachments.length > 1 ? 'Photos' : 'Photo';
    return `${prefix}: ${label}`;
  }
  if (message.message_type && message.message_type !== 'text') {
    return `${prefix}: ${message.message_type}`;
  }

  // If it was text but encrypted/empty, give a neutral prompt
  if (message.message_type === 'text') {
    return `${prefix}: New message`;
  }

  return prefix;
}

export function getConversationSubtitle(conversation, currentUserId) {
  if (conversation.conversation_type === 'club_announcement') {
    return 'Sent to all members';
  }

  return describeMessage(conversation.last_message, conversation, currentUserId);
}

export function getThreadSubtitle(conversation) {
  if (conversation.conversation_type === 'club_announcement') {
    return 'Sent to all members';
  }
  if (conversation.conversation_type === 'direct') {
    return 'Direct message';
  }
  const count = conversation.participants?.length || 0;
  return `${count} participant${count === 1 ? '' : 's'}`;
}

export function formatConversationTime(timestamp, now = new Date()) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.floor((startOfDay(now) - startOfDay(date)) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return formatTimeLabel(timestamp);
  return formatDateLabel(timestamp, now);
}

const conversationMeta = {
  getConversationTitle,
  getConversationAvatarUrl,
  getConversationInitials,
  getConversationSubtitle,
  getThreadSubtitle,
  formatConversationTime,
};

export default conversationMeta;
